import { Menu, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';

/**
 * Mobile (< 1024px) navigation: a hamburger that opens a left drawer with
 * whatever `AppShell` puts in it — the same logo, top slot (company switch
 * or a back-to-app link) and nav the desktop aside shows, so small screens
 * never see a different shell, just a collapsed one. Render-prop `children`
 * gets a `close` callback so a clicked nav link can dismiss the drawer.
 */
export function MobileDrawer({ children }: { children: (close: () => void) => ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const focusables = panel?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    focusables?.[0]?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab' || !focusables || focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <div className="lg:hidden">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls="mobile-drawer"
        aria-label={open ? t('shell.closeMainMenu') : t('shell.openMainMenu')}
        onClick={() => setOpen((value) => !value)}
        className="border-line text-ink rounded-pill focus-visible:ring-accent focus-visible:ring-offset-paper flex size-9 items-center justify-center border focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        {open ? (
          <X className="size-5" aria-hidden="true" />
        ) : (
          <Menu className="size-5" aria-hidden="true" />
        )}
      </button>

      {open && (
        <>
          <div className="bg-ink/40 fixed inset-0 z-30" onClick={close} aria-hidden="true" />
          <div
            id="mobile-drawer"
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('shell.mainNav')}
            className={cn(
              'bg-paper fixed inset-y-0 left-0 z-30 flex w-72 max-w-[85vw] flex-col gap-6 overflow-y-auto p-6',
              'transition-transform motion-reduce:transition-none',
            )}
          >
            {children(close)}
          </div>
        </>
      )}
    </div>
  );
}
